import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Loader2, FileDown, Upload, ClipboardType, ShieldCheck, RotateCcw, Timer, Presentation } from 'lucide-react';

const C = { navy: '#0D1B2A', blue: '#1A4C7C', teal: '#00B4D8', slate: '#5C6B7A', green: '#1D9E75', amber: '#B27516', red: '#C0392B', line: 'rgba(136,150,168,0.12)' };
const DECISION_COLOR = { APPROVE: C.green, CONDITIONS: C.teal, REFER: C.amber, DECLINE: C.red };
const fmt$ = v => (v || v === 0) ? `$${Math.round(v).toLocaleString()}` : '—';

function ConfidenceBar({ value, color }) {
  const pct = Math.round((value || 0) * 100);
  return (
    <div className="flex items-center gap-2">
      <div className="h-1.5 w-16 rounded-full" style={{ background: '#E8EDF2' }}>
        <div className="h-1.5 rounded-full" style={{ width: `${pct}%`, background: color || C.teal }} />
      </div>
      <span className="text-[11px] font-mono" style={{ color: C.slate }}>{pct}%</span>
    </div>
  );
}

function ResultPanel({ run, api, onReset }) {
  const color = DECISION_COLOR[run.decision] || C.slate;
  const fields = Object.entries(run.extracted_fields || {});
  const downloadPdf = async () => {
    try {
      const resp = await api.get(`/demo/decision/${run.run_id}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement('a');
      a.href = url; a.download = `Decision_Rationale_${run.run_id}.pdf`; a.click();
      URL.revokeObjectURL(url);
      toast.success('Decision Rationale PDF downloaded — something they keep');
    } catch { toast.error('Could not download the rationale PDF'); }
  };
  return (
    <div className="space-y-4 stagger-in" data-testid="demo-result-panel">
      <Card className="p-6 rounded-md bg-white" style={{ border: `1px solid ${C.line}`, borderLeft: `4px solid ${color}` }}>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-baseline gap-5">
            <span className="text-3xl font-bold tracking-tight" style={{ color }} data-testid="demo-decision-chip">{run.decision}</span>
            <div className="text-sm" style={{ color: C.slate }}>
              Score <b style={{ color: C.navy }} data-testid="demo-score">{run.composite_score?.toLocaleString()}</b>
              {run.stp && <span className="ml-3 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider" style={{ background: 'rgba(29,158,117,0.1)', color: C.green }}>straight-through eligible</span>}
            </div>
          </div>
          <div className="flex items-center gap-2 text-sm" style={{ color: C.slate }} data-testid="demo-elapsed">
            <Timer size={15} style={{ color: C.teal }} />
            Decided in <b style={{ color: C.navy }}>{run.elapsed_seconds}s</b>
          </div>
        </div>
        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4 pt-4" style={{ borderTop: `1px solid ${C.line}` }}>
          <div>
            <p className="text-[10px] uppercase tracking-wider font-medium" style={{ color: C.slate }}>Model confidence</p>
            <div className="mt-1" data-testid="demo-confidence"><ConfidenceBar value={run.confidence} color={color} /></div>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider font-medium" style={{ color: C.slate }}>Indicative premium</p>
            <p className="font-mono text-lg font-bold" style={{ color: C.navy }} data-testid="demo-premium">{fmt$(run.premium)}</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider font-medium" style={{ color: C.slate }}>Total payable</p>
            <p className="font-mono text-lg font-bold" style={{ color: C.navy }}>{fmt$(run.total_payable)}</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider font-medium" style={{ color: C.slate }}>Model version</p>
            <p className="text-xs font-mono mt-1.5" style={{ color: C.slate }}>{run.model_version}</p>
          </div>
        </div>
      </Card>

      {run.rationale && (
        <Card className="p-5 rounded-md bg-white" style={{ border: `1px solid ${C.line}` }}>
          <p className="text-[10px] uppercase tracking-[0.15em] font-medium mb-2" style={{ color: C.slate, fontFamily: 'Georgia, serif' }}>Written rationale</p>
          <p className="text-sm leading-relaxed" style={{ color: C.navy }} data-testid="demo-rationale">{run.rationale}</p>
        </Card>
      )}

      {(run.rules || []).length > 0 && (
        <Card className="p-5 rounded-md bg-white" style={{ border: `1px solid ${C.line}` }}>
          <p className="text-[10px] uppercase tracking-[0.15em] font-medium mb-2" style={{ color: C.slate, fontFamily: 'Georgia, serif' }}>Why this was not straight-through</p>
          <div className="space-y-1.5" data-testid="demo-rules">
            {run.rules.map((r, i) => (
              <div key={i} className="flex gap-2 text-xs"><b className="font-mono shrink-0" style={{ color: C.amber }}>{r.rule}</b><span style={{ color: C.slate }}>{r.desc}</span></div>
            ))}
          </div>
        </Card>
      )}

      {fields.length > 0 && (
        <Card className="p-5 rounded-md bg-white" style={{ border: `1px solid ${C.line}` }}>
          <p className="text-[10px] uppercase tracking-[0.15em] font-medium mb-2" style={{ color: C.slate, fontFamily: 'Georgia, serif' }}>What the model read — with stated confidence</p>
          <table className="w-full text-xs" data-testid="demo-extracted-fields">
            <thead><tr className="text-left text-[10px] uppercase tracking-wider" style={{ color: C.slate }}>
              <th className="py-1.5 pr-2">Field</th><th className="py-1.5 pr-2">Value</th><th className="py-1.5 pr-2">Confidence</th><th className="py-1.5">Supporting text</th></tr></thead>
            <tbody>
              {fields.map(([k, v]) => (
                <tr key={k} style={{ borderTop: `1px solid ${C.line}` }}>
                  <td className="py-2 pr-2 font-medium" style={{ color: C.navy }}>{k.replace(/_/g, ' ')}</td>
                  <td className="py-2 pr-2 font-mono" style={{ color: C.navy }}>{String(v.value)}</td>
                  <td className="py-2 pr-2"><ConfidenceBar value={v.confidence} color={v.confidence < 0.6 ? C.amber : C.teal} /></td>
                  <td className="py-2 italic" style={{ color: C.slate }}>{(v.source || '').slice(0, 90)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {(run.low_confidence_fields || []).length > 0 && (
            <p className="text-[11px] mt-2" style={{ color: C.amber }}>
              The model flagged {run.low_confidence_fields.length} low-confidence field(s): {run.low_confidence_fields.join(', ')} — it says so instead of guessing.
            </p>
          )}
        </Card>
      )}

      <div className="flex gap-2">
        <Button onClick={downloadPdf} className="rounded-md text-xs" style={{ background: C.navy }} data-testid="demo-rationale-pdf-btn">
          <FileDown className="w-3.5 h-3.5 mr-1.5" /> Download Decision Rationale (PDF)
        </Button>
        <Button variant="outline" onClick={onReset} className="rounded-md text-xs" data-testid="demo-reset-btn">
          <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Run another
        </Button>
      </div>
    </div>
  );
}

export default function LiveDecisionDemoPage() {
  const api = useApi();
  const navigate = useNavigate();
  const [classes, setClasses] = useState({});
  const [line, setLine] = useState('sme_commercial');
  const [mode, setMode] = useState('paste');
  const [text, setText] = useState('');
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [run, setRun] = useState(null);
  const timerRef = useRef(null);

  useEffect(() => {
    api.get('/insurance-classes').then(r => setClasses(r.data || {})).catch(() => {});
  }, [api]);

  useEffect(() => () => clearInterval(timerRef.current), []);

  const execute = async () => {
    setBusy(true); setElapsed(0);
    const t0 = Date.now();
    timerRef.current = setInterval(() => setElapsed(((Date.now() - t0) / 1000).toFixed(1)), 100);
    try {
      let data;
      if (mode === 'paste') {
        ({ data } = await api.post('/demo/decision', { text, line }));
      } else {
        const fd = new FormData();
        fd.append('line', line); fd.append('file', file);
        ({ data } = await api.post('/demo/decision/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } }));
      }
      setRun(data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Demo run failed'); }
    clearInterval(timerRef.current);
    setBusy(false);
  };

  const canRun = line && (mode === 'paste' ? text.trim().length >= 30 : !!file);

  return (
    <div className="space-y-4 max-w-4xl" data-testid="live-decision-demo-page">
      <div className="flex items-start gap-2 p-3 rounded-md" style={{ background: 'rgba(0,180,216,0.06)', border: '1px solid rgba(0,180,216,0.25)' }} data-testid="demo-privacy-banner">
        <ShieldCheck size={15} className="mt-0.5 shrink-0" style={{ color: C.teal }} />
        <p className="text-xs flex-1" style={{ color: C.navy }}>
          <b>Bring your own submission.</b> Nothing here is pre-loaded — paste or upload the prospect's real broker email, PDF or spreadsheet
          and watch the decision, confidence and written rationale come back in under a minute. Demo runs never enter the live book
          and are deleted on request in one action.
        </p>
        <Button size="sm" variant="outline" onClick={() => navigate('/present')} className="rounded-md text-xs shrink-0" data-testid="demo-present-btn">
          <Presentation className="w-3.5 h-3.5 mr-1.5" /> Present on big screen
        </Button>
      </div>

      {!run && (
        <Card className="p-5 rounded-md bg-white space-y-4" style={{ border: `1px solid ${C.line}` }}>
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex rounded-md overflow-hidden" style={{ border: `1px solid ${C.line}` }}>
              <button onClick={() => setMode('paste')} data-testid="demo-mode-paste"
                className="px-3 py-1.5 text-xs font-medium flex items-center gap-1.5"
                style={mode === 'paste' ? { background: C.navy, color: '#fff' } : { color: C.slate }}>
                <ClipboardType size={13} /> Paste text
              </button>
              <button onClick={() => setMode('upload')} data-testid="demo-mode-upload"
                className="px-3 py-1.5 text-xs font-medium flex items-center gap-1.5"
                style={mode === 'upload' ? { background: C.navy, color: '#fff' } : { color: C.slate }}>
                <Upload size={13} /> Upload document
              </button>
            </div>
            <select value={line} onChange={e => setLine(e.target.value)} data-testid="demo-line-select"
              className="text-xs rounded-md px-2.5 py-2 bg-white" style={{ border: `1px solid ${C.line}`, color: C.navy }}>
              {Object.keys(classes).length === 0 && <option value={line}>Loading classes…</option>}
              {Object.values(classes).filter(c => c.active).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>

          {mode === 'paste' ? (
            <Textarea value={text} onChange={e => setText(e.target.value)} rows={8} data-testid="demo-text-input"
              placeholder={'Paste the broker email or submission text here…\n\ne.g. "Hi team, please quote Public Liability for Bella\'s Espresso Bar Pty Ltd (cafe, Newtown NSW 2042). Annual turnover about $850,000, 6 staff, $20M limit…"'}
              className="rounded-sm text-sm font-mono" />
          ) : (
            <div>
              <input type="file" accept=".pdf,.csv,.xlsx,.txt,.eml,.msg" data-testid="demo-file-input"
                onChange={e => setFile(e.target.files?.[0] || null)}
                className="text-xs file:mr-3 file:px-3 file:py-1.5 file:rounded-md file:border-0 file:text-xs file:font-medium file:bg-[#0D1B2A] file:text-white" />
              <p className="text-[11px] mt-1.5" style={{ color: C.slate }}>PDF, spreadsheet or email export — read live, never stored in the live book. 10MB limit.</p>
            </div>
          )}

          <Button onClick={execute} disabled={!canRun || busy} className="rounded-md" style={{ background: C.teal, color: C.navy, fontWeight: 600 }} data-testid="demo-run-btn">
            {busy ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Deciding… {elapsed}s</> : 'Get the decision'}
          </Button>
        </Card>
      )}

      {run && <ResultPanel run={run} api={api} onReset={() => { setRun(null); setText(''); setFile(null); }} />}
    </div>
  );
}
